import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnChanges, OnInit, output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { GridActions, GridOverrides, TeamSettingFormSchema, TeamSettingSection } from '@mds/common';
import { ButtonComponent, GridActionsComponent, GridModule, GridNoRowsComponent } from '@mds/ui';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { ToastrService } from '@services/toaster.service';
import { ColDef, GridApi, RowNode } from 'ag-grid-enterprise';
import { DataSourcesService } from './services/data-sources.service';
import { DataSourceFormDialogComponent } from './data-source-form-dialog.component';
import { DataSourceRow, DataSourcesResponse } from './models/data-source.model';
import { CONNECTOR_LABELS, FREQUENCY_LABELS, PURPOSE_LABELS } from './constants/data-source.constants';

@Component({
  selector: 'app-data-sources-settings',
  standalone: true,
  imports: [GridModule, ButtonComponent, MatDialogModule],
  templateUrl: './data-sources-settings.component.html',
  styleUrls: ['./data-sources-settings.component.scss'],
  providers: [DataSourcesService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataSourcesSettingsComponent implements OnInit, OnChanges {
  teamRid = input<string>('');
  section = input<TeamSettingSection>();
  reload = output<void>();

  overrides: GridOverrides;
  isProcessing = false;
  private gridApi: GridApi;
  private skipNextChange = false;
  private dataSourcesService = inject(DataSourcesService);
  private toastr = inject(ToastrService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['section']) {
      if (this.skipNextChange) {
        this.skipNextChange = false;
        return;
      }
      this.init();
      this.gridApi?.setGridOption('loading', false);
    }
  }

  onGridReady(api: GridApi): void {
    this.gridApi = api;
  }

  init(): void {
    const sectionValue = this.section();

    if (sectionValue) {
      // Parent provided section data (from composer route)
      const rowData = this.addActionsToRows((sectionValue.lines || []) as unknown as DataSourceRow[]);
      this.buildGridFromData(rowData, sectionValue.formSchema);
    } else {
      // No section from parent — load directly from data-sources API
      this.loadFromApi();
    }
  }

  private loadFromApi(): void {
    this.dataSourcesService
      .getDataSources()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: DataSourcesResponse) => {
          const rowData = this.addActionsToRows(res.data_sources || []);
          this.buildGridFromData(rowData);
        }
      });
  }

  /**
   * Fetch fresh data from the dedicated API and update the grid directly.
   * Sets `skipNextChange` so the subsequent ngOnChanges from the parent's
   * reload handler does not trigger a redundant second fetch.
   */
  private refreshGrid(): void {
    this.isProcessing = true;
    this.gridApi?.setGridOption('loading', true);
    this.dataSourcesService
      .getDataSources()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: DataSourcesResponse) => {
          const rowData = this.addActionsToRows(res.data_sources || []);
          if (this.gridApi) {
            this.gridApi.setGridOption('rowData', rowData);
            this.gridApi.setGridOption('loading', false);
          } else {
            const sectionValue = this.section();
            this.buildGridFromData(rowData, sectionValue?.formSchema);
          }
          this.isProcessing = false;
          this.skipNextChange = true;
        },
        error: () => {
          this.gridApi?.setGridOption('loading', false);
          this.isProcessing = false;
        }
      });
  }

  private addActionsToRows(rows: DataSourceRow[]): DataSourceRow[] {
    return rows.map((row: DataSourceRow) => ({
      ...row,
      actions: { edit: true, delete: true }
    }));
  }

  private buildGridFromData(rowData: DataSourceRow[], formSchema?: TeamSettingFormSchema[]): void {
    const columnDefs = this.buildColumnDefs(formSchema);
    this.overrides = {
      columnDefs,
      rowData,
      defaultColDef: {
        flex: 1,
        minWidth: 80,
        sortable: true,
        filter: true,
        resizable: true
      },
      noRowsOverlayComponent: GridNoRowsComponent,
      noRowsOverlayComponentParams: {
        title: 'No data sources configured'
      }
    };
  }

  private buildColumnDefs(formSchema?: TeamSettingFormSchema[]): ColDef[] {
    const columns: ColDef[] = [
      { field: 'name', headerName: 'Name', minWidth: 150 },
      {
        field: 'connector_type',
        headerName: 'Type',
        minWidth: 120,
        cellRenderer: (params: { value: string }) => CONNECTOR_LABELS[params.value] || params.value || ''
      },
      {
        field: 'enabled',
        headerName: 'Enabled',
        minWidth: 80,
        cellRenderer: (params: { value: boolean }) => (params.value ? 'Yes' : 'No')
      },
      {
        field: 'purpose',
        headerName: 'Purpose',
        minWidth: 120,
        cellRenderer: (params: { value: string }) => PURPOSE_LABELS[params.value] || params.value || ''
      },
      {
        field: 'refresh_frequency',
        headerName: 'Refresh',
        minWidth: 100,
        cellRenderer: (params: { value: string }) => FREQUENCY_LABELS[params.value] || params.value || ''
      },
      { field: 'status', headerName: 'Status', minWidth: 100 }
    ];

    columns.push({
      headerName: 'Actions',
      field: 'actions',
      lockPosition: true,
      pinned: 'right',
      width: 120,
      cellRenderer: GridActionsComponent,
      cellRendererParams: {
        onClick: (params: { event: GridActions; node: RowNode }) => {
          this.gridActionsEvent(params);
        }
      }
    });

    return columns;
  }

  gridActionsEvent(params: { event: GridActions; node: RowNode }): void {
    switch (params.event) {
      case GridActions.EDIT:
        this.openEditDialog(params.node.data);
        break;
      case GridActions.DELETE:
        this.deleteDataSource(params.node.data.rid);
        break;
    }
  }

  openAddDialog(): void {
    this.openFormDialog({ mode: 'create' });
  }

  openEditDialog(dataSource: DataSourceRow): void {
    this.openFormDialog({ mode: 'edit', dataSource });
  }

  private openFormDialog(opts: { mode: 'create' | 'edit'; dataSource?: DataSourceRow }): void {
    const sectionValue = this.section();
    const dialogRef = this.dialog.open(DataSourceFormDialogComponent, {
      width: '600px',
      autoFocus: false,
      data: {
        teamRid: this.teamRid(),
        formSchema: sectionValue?.formSchema,
        ...opts
      }
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result) {
          this.refreshGrid();
          this.reload.emit();
        }
      });
  }

  deleteDataSource(rid: string): void {
    if (!rid || this.isProcessing) return;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Delete Data Source',
        text: 'Are you sure you want to delete this data source?'
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed) return;

        this.isProcessing = true;
        this.gridApi?.setGridOption('loading', true);
        this.dataSourcesService
          .deleteDataSource(rid)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.toastr.showMessage('Data source deleted');
              this.refreshGrid();
              this.reload.emit();
            },
            error: () => {
              this.toastr.showMessage('Failed to delete data source', 2000);
              this.gridApi?.setGridOption('loading', false);
              this.isProcessing = false;
            }
          });
      });
  }
}
